// ─────────────────────────────────────────────
//  doctorCoverage — the instant. "At minute T, how many rooms are running,
//  how many of them need supervising, and how many doctors are here?"
//
//  A LEAF. It imports nothing, which is the whole point: `timeline.js` owns the
//  day (and the time parsers), `doctorSteps.js` owns the projection across it,
//  and both need to ask this same question at a single minute. Putting the
//  answer here is what stops there being two of it — the drift that already
//  produced THREE copies of the supervision ratio in this codebase
//  (ROOMS_PER_DOCTOR, facilityRules.supervisionRatio, DoctorAssignFlow.MAX_ROOMS).
//
//  ⚠️ THE TIME PARSER IS INJECTED, not imported. `hmToMinutes` lives in
//  timeline.js, and importing it here would close the loop timeline.js →
//  doctorCoverage.js → timeline.js. A second copy of the parser would be worse
//  than the cycle, so the caller hands its own in.
// ─────────────────────────────────────────────

// Rooms running at T, and which of them are solo.
//
// A room runs on [startMin, endMin + bufferMin) — the same 15-minute tail
// `buildStaffing` and `buildDoctorStaffing` already apply, because a room
// finishing at 12:00 still needs a doctor at 12:00.
//
// ⚠️ A ROOM IS COUNTED ONCE however many cases it runs. `placed` is one row per
// CASE, so a room with three cases back-to-back is three rows; counting rows
// would triple it and demand three times the doctors.
//
// A case whose minutes are missing or inverted is DROPPED, not counted. That is
// already what the raw predicate did (`null <= T` is true but `null > T - 15` is
// false), but stating it means a future edit cannot quietly reverse it: a row
// the importer could not read is not evidence of a running room.
export function roomsRunningAt(placed, T, bufferMin, endedRoomIds = null) {
  const rooms = new Set();
  const soloRooms = new Set();
  const soloDoctorIds = new Set();
  const soloDoctorNames = new Set();
  const buffer = Number.isFinite(bufferMin) ? bufferMin : 0;
  for (const p of Array.isArray(placed) ? placed : []) {
    if (!p) continue;
    const start = p.startMin;
    const end = p.endMin;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
    if (!(start <= T && end > T - buffer)) continue;
    // ⚠️ A CANCELLED ROOM IS NOT RUNNING. The lane still exists — the assignment
    // row was never deleted — so without this the board went on demanding a
    // supervising doctor for a room whose case is not happening.
    if (endedRoomIds && p.roomId && endedRoomIds.has(p.roomId)) continue;
    rooms.add(p.roomId);
    // A solo case ties its doctor up IN the room: they cannot supervise, and
    // their own room needs no supervising doctor. Both halves matter.
    if (p.solo) {
      soloRooms.add(p.roomId);
      if (p.attendingId) soloDoctorIds.add(p.attendingId);
      if (p.attendingName) soloDoctorNames.add(String(p.attendingName).toLowerCase());
    }
  }
  return { rooms, soloRooms, soloDoctorIds, soloDoctorNames };
}

// Supervising doctors needed for n rooms at a given ratio. Ceil, because a
// fifth room at 1:4 needs a second doctor for that one room.
//
// The ratio is a FACILITY RULE (facilityRules.supervisionRatio, clamped 1–4),
// not a constant. A bad or missing value falls back to 4 rather than dividing
// by zero and demanding infinite doctors.
export function requiredDoctors(supervisionRooms, ratio) {
  const r = Number.isFinite(Number(ratio)) && Number(ratio) > 0 ? Number(ratio) : 4;
  const n = Math.max(0, Number(supervisionRooms) || 0);
  return Math.ceil(n / r);
}

// Roster doctors → a deduped, time-resolved pool.
//
// `hmToMin` parses "HH:MM"; pass timeline.js's `hmToMinutes`. Without one the
// shift-window fields are simply ignored and only a numeric `leaveMin` counts,
// which is what the unit tests exercise.
//
// A doctor's leaving minute is, in order: an explicit `leaveMin` (what
// `attachLocumLeaves` stamps on a locum with a locked leaving time), else the
// end of their NOTES-row cover window. `coverEndNextDay` (Night Float,
// 18:00 → 07:00) means they never leave within the day, so it resolves to null.
export function prepareDoctors(rosterDoctors, { hmToMin } = {}) {
  const parse = typeof hmToMin === "function" ? hmToMin : () => null;
  const byKey = new Map();
  for (const d of Array.isArray(rosterDoctors) ? rosterDoctors : []) {
    const key = d && (d.id || d.name);
    if (!key || byKey.has(key)) continue;
    // ⚠️ A DEPARTED DOCTOR IS NOT AVAILABLE. `withDeparted` marks them upstream
    // (the same flag BreakEnginePanel reads for CRNAs); without this they were
    // counted as on call and as releasable after they had gone home, which
    // INFLATES `available` and therefore hides a shortage rather than inventing
    // one. Leaving is not the same as a cover window ending, so it cannot ride
    // on leaveMin: a doctor can leave at any minute, for any reason.
    if (d.departed) continue;
    const coverStartMin = parse(d?.coverStart);
    const coverEndMin = d?.coverEndNextDay ? null : parse(d?.coverEnd);
    const leaveMin = Number.isFinite(d?.leaveMin)
      ? d.leaveMin
      : coverEndMin != null
        ? coverEndMin
        : null;
    byKey.set(key, { key, name: d.name || "Doctor", leaveMin, startMin: coverStartMin });
  }
  return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name));
}

// Which prepared doctors are here at T, split into those free to supervise and
// those locked in a solo case.
//
// Present at T = on shift (no start, or start ≤ T) AND not yet gone (no leave,
// or leaving strictly after T).
//
// The solo split is PER INSTANT, so a doctor whose solo case ends returns to
// the supervision pool at the next step on their own.
export function doctorsAvailableAt(prepared, T, { soloDoctorIds, soloDoctorNames } = {}) {
  const ids = soloDoctorIds || new Set();
  const names = soloDoctorNames || new Set();
  const present = (Array.isArray(prepared) ? prepared : []).filter(
    (d) => d && (d.startMin == null || d.startMin <= T) && (d.leaveMin == null || d.leaveMin > T)
  );
  const isSolo = (d) => ids.has(d.key) || names.has(String(d.name || "").toLowerCase());
  const soloList = present.filter(isSolo);
  const doctorList = present.filter((d) => !isSolo(d));
  return { available: doctorList.length, doctorList, soloList };
}
